import {
  Alert as BillingAlert,
  AlertCode,
  AlertLinkType,
  BillingStatus,
  CardRegistrationType,
  CompanyQuery,
  useCardRegistrationUrlV2LazyQuery,
} from "@/generated/graphql_billing";
import { FAQ_URL } from "@/libs/constants";
import { color } from "@/styles/theme";
import { css } from "@emotion/react";
import {
  Button,
  Cell,
  Font,
  Icon,
  List,
  Modal,
  ToolTip,
} from "@moneyforward/mfx_biz_react_components";
import { addMonths, endOfDay, format, startOfDay, subMonths } from "date-fns";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Alert, Badge } from "../atoms";
import { NoContract } from "../molecules";
import { TAB } from "@/components/pages";
import { useRouter } from "next/router";
import { IssueInvoiceModal } from "@/components/atoms/IssueInvoiceModal";
import { GetCompanyInfoQuery } from "@/generated/graphql";
import useRollbarUtil from "../hooks/useRollbarUtil";
import config from "@/libs/config";

type Props = {
  company: CompanyQuery["company"];
  setActiveTab: Dispatch<SetStateAction<number>>;
  alerts: BillingAlert[];
  now: Date;
  companyInfo: GetCompanyInfoQuery["company"];
};

export const InvoiceDetail = (props: Props) => {
  const { getNotifyErrorHandlerForGraphQL } = useRollbarUtil();
  const { setActiveTab, alerts, now } = props;
  const company = props.company;
  const thisMonth = format(now, "yyyyMM");
  const nextMonth = format(addMonths(now, 1), "yyyyMM");
  const lastThreeMonth = [...Array(3)].map((_, index) =>
    format(subMonths(now, index + 1), "yyyyMM")
  );

  const thisMonthBilling = company.billings
    ? company.billings.find((bill) => bill.usingMonth === thisMonth)
    : "";
  const lastThreeMonthBilling = company.billings
    ? company.billings
        .filter((bill) =>
          lastThreeMonth.find((month) => bill.usingMonth === month)
        )
        .sort((a, b) => b.usingMonth.localeCompare(a.usingMonth))
    : [];

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>();
  const modalBilling = company.billings
    ? company.billings.find((bill) => bill.id === selectedId)
    : "";
  const tax = modalBilling && modalBilling.taxAmount;
  const amount = modalBilling && modalBilling.amount;

  const issuableBillingStatuses = [
    BillingStatus.Init,
    BillingStatus.InRequest,
    BillingStatus.Ok,
    BillingStatus.Ng,
  ];
  const issuableBillings = company.billings
    ? company.billings.filter((bill) =>
        issuableBillingStatuses.includes(bill.status)
      )
    : [];
  const [isIssueModalOpen, setIsIssueModalOpen] = useState<boolean>(false);

  const IssueInvoiceButton = () => {
    // This was originally implemented to allow us to temporarily disable the
    // issue invoice button while we were waiting for a new company registration
    // number to be issued.
    //
    // The company registration number has since been issued and to enable the
    // button, the below date is set to a date far into the future.
    //
    // If a similar need arises in the future, update the tool tip text and set
    // the below date from which the button should be disabled.
    const disableIssueInvoiceButtonAfter = new Date(
      "2099-01-01T00:00:00+09:00"
    );

    if (now < disableIssueInvoiceButtonAfter) {
      return (
        <Button variant="secondary" onClick={() => setIsIssueModalOpen(true)}>
          <Font variant="mb">請求書の発行</Font>
        </Button>
      );
    }
    return (
      <ToolTip
        placement={"left"}
        text={
          "適格請求書発行事業者の登録申請中のため、登録番号の取得完了まで請求書の発行を停止します。"
        }
      >
        <div>
          <Button variant="secondary" onClick={() => false} disabled={true}>
            <Font variant="mb">請求書の発行</Font>
            <Icon name="attention" width={18} />
          </Button>
        </div>
      </ToolTip>
    );
  };

  const modalBody = (
    <>
      {modalBilling && (
        <div css={modalContainer}>
          <Font variant="mb">
            {Number(modalBilling.usingMonth.slice(-2))}月ご利用料金
          </Font>
          {modalBilling.billingDetails.map((detail) => (
            <div css={modalList} key={detail.id}>
              <Font>{detail.content}</Font>
              <Font>{detail.amount.toLocaleString()}円</Font>
            </div>
          ))}
          <div css={border} />
          <div css={modalBottom}>
            <Font variant="s">小計：{amount.toLocaleString()}円</Font>
          </div>
          <div css={modalBottom}>
            <Font variant="s">消費税：{tax.toLocaleString()}円</Font>
          </div>
          <div css={modalBottom}>
            <Font>
              請求金額：
              {(amount + tax).toLocaleString()}円
            </Font>
          </div>
        </div>
      )}
    </>
  );

  const modalFooterButton = (
    <Button
      type="button"
      variant="primary"
      onClick={() => setIsModalOpen(false)}
    >
      閉じる
    </Button>
  );

  const router = useRouter();
  const { app } = router.query;
  const [cardRegistrationUrl, setCardRegistrationUrl] = useState<string>();
  const [cardRegistrationUrlQuery] = useCardRegistrationUrlV2LazyQuery({
    onCompleted: async (data) => {
      if (data.cardRegistrationUrlV2) {
        setCardRegistrationUrl(data.cardRegistrationUrlV2);
      }
    },
    onError: getNotifyErrorHandlerForGraphQL(
      "Failed to get card registration url"
    ),
    context: {
      api: "billing",
    },
    variables: {
      bankName: config.BANK_SYSTEM_NAME,
      cardRegistrationType: CardRegistrationType.ManageContract,
      app: app ? String(app) : "",
    },
  });

  useEffect(() => {
    (async () => {
      await cardRegistrationUrlQuery();
    })();
  }, []);

  const reversalNgAlerts = alerts.filter(
    (alert) => alert.code === AlertCode.ReversalNg
  );

  const getAlerts = (
    alerts: BillingAlert[],
    alertCode: AlertCode,
    linkURL: string
  ) =>
    alerts
      .filter((alert) => alert.code === alertCode)
      .map((alert) => ({
        alertID: alert.alertID,
        alertType: alert.alertType,
        linkType: AlertLinkType.External,
        linkURL,
        message: alert.message,
      }));
  const firstBillingNgAlerts = getAlerts(
    alerts,
    AlertCode.FirstBillingNg,
    cardRegistrationUrl
  );
  const secondBillingNgAlerts = getAlerts(
    alerts,
    AlertCode.SecondBillingNg,
    FAQ_URL
  );
  const suspendedAlerts = getAlerts(alerts, AlertCode.Suspended, FAQ_URL);

  const yyyySlashMM = (yyyyMM: string) =>
    yyyyMM.replace(/^(\d{4})(\d{2})$/, "$1/$2");

  if (
    company.availableServices.length > 0 ||
    (company.billings && company.billings.length > 0)
  ) {
    return (
      <>
        {isModalOpen && (
          <div css={overlay}>
            <Modal
              title="料金明細"
              body={modalBody}
              zIndex={20}
              footerButtons={modalFooterButton}
              onClose={() => setIsModalOpen(false)}
            />
          </div>
        )}
        {isIssueModalOpen && (
          <IssueInvoiceModal
            active={isIssueModalOpen}
            issuableBillings={issuableBillings}
            companyName={props.companyInfo.companyNameStoreName}
            onClickCancel={() => setIsIssueModalOpen(false)}
          />
        )}
        <div css={container}>
          {(company.planContract ||
            company.canceledPlanContracts.find(
              (plan) =>
                startOfDay(new Date(plan.usingDateFrom)) <= now &&
                (!plan.usingDateTo ||
                  now <= endOfDay(new Date(plan.usingDateTo)))
            )) && (
            <div css={containerItem}>
              <Font variant="mb">今月のご利用料金</Font>
              <div css={box}>
                <div css={flexBox}>
                  <div css={flexBoxContent(40)}>
                    <div>
                      <Font css={{ color: color.gray600 }}>
                        {thisMonthBilling
                          ? Number(thisMonthBilling.usingMonth.slice(-2))
                          : Number(thisMonth.slice(-2))}
                        月ご利用料金
                      </Font>
                      <div css={flexBoxContentText}>
                        <div css={smallBadge}>
                          <Badge fill={false} variant="secondary" text="予定" />
                        </div>
                        <Font variant="lb">
                          {thisMonthBilling
                            ? (
                                thisMonthBilling.amount +
                                thisMonthBilling.taxAmount
                              ).toLocaleString()
                            : company.currentTotalAmount.toLocaleString()}
                          円
                        </Font>
                        <Font variant="s">(税込)</Font>
                      </div>
                    </div>
                    <div>
                      <Font css={{ color: color.gray600 }}>請求月</Font>
                      <div>
                        <Font variant="lb">
                          {thisMonthBilling
                            ? yyyySlashMM(
                                format(
                                  new Date(thisMonthBilling.billingOn),
                                  "yyyyMM"
                                )
                              )
                            : yyyySlashMM(nextMonth)}
                        </Font>
                      </div>
                    </div>
                  </div>
                  <Font
                    onClick={() => setActiveTab(TAB.contract.index)}
                    css={linkButton}
                  >
                    契約内容
                  </Font>
                </div>
              </div>
            </div>
          )}
          <div css={containerItem}>
            <Font variant="mb">過去3ヶ月の請求履歴</Font>
            {firstBillingNgAlerts.length > 0 && (
              <Alert
                alerts={firstBillingNgAlerts}
                linkText="カード情報の変更・確認"
              />
            )}
            {secondBillingNgAlerts.length > 0 && (
              <Alert alerts={secondBillingNgAlerts} linkText="詳しくはこちら" />
            )}
            {suspendedAlerts.length > 0 && (
              <Alert alerts={suspendedAlerts} linkText="詳しくはこちら" />
            )}
            {lastThreeMonthBilling.length > 0 ? (
              <div>
                <List
                  isHeader
                  gridTemplateColumns="25% 15% 15% 25% 10%"
                  firstSpacing={16}
                >
                  <Cell>内容</Cell>
                  <Cell>金額（税込）</Cell>
                  <Cell>請求月</Cell>
                  <Cell>状態</Cell>
                  <Cell>料金明細</Cell>
                </List>
                {lastThreeMonthBilling.map((billing) => (
                  <div key={billing.id}>
                    <List
                      isHeader
                      gridTemplateColumns="25% 15% 15% 25% 10%"
                      firstSpacing={16}
                      style={{ height: "48px" }}
                    >
                      <Cell>
                        <Font css={{ color: color.black }}>
                          {Number(billing.usingMonth.slice(-2))}月ご利用料金
                        </Font>
                      </Cell>
                      <Cell textAlign="right">
                        <Font css={{ color: color.black }}>
                          {(
                            billing.amount + billing.taxAmount
                          ).toLocaleString()}
                        </Font>
                      </Cell>
                      <Cell>
                        <Font css={{ color: color.black }}>
                          {format(new Date(billing.billingOn), "yyyy/MM")}
                        </Font>
                      </Cell>
                      <Cell>
                        <div css={largeBadge}>
                          <Badge
                            fill={billing.status === BillingStatus.Ok}
                            variant={
                              billing.status === BillingStatus.Ng
                                ? "destructive"
                                : "secondary"
                            }
                            text={
                              billing.status === BillingStatus.Ok
                                ? "支払済"
                                : billing.status === BillingStatus.Init ||
                                  billing.status === BillingStatus.InRequest
                                ? "未請求"
                                : billing.status === BillingStatus.Unclaimed ||
                                  billing.status === BillingStatus.Canceled
                                ? "請求無し"
                                : "決済エラー"
                            }
                            paddingVertical={2}
                          />
                        </div>
                      </Cell>
                      <Cell>
                        <Font
                          onClick={() => {
                            setSelectedId(billing.id);
                            setIsModalOpen(true);
                          }}
                          css={linkButton}
                        >
                          料金明細
                        </Font>
                      </Cell>
                    </List>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <Font variant="m">請求履歴はありません</Font>
              </div>
            )}
            {issuableBillings.length > 0 && (
              <div
                css={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                <IssueInvoiceButton />
              </div>
            )}
          </div>
          {company.cardRegistered && (
            <div css={containerItem}>
              <Font variant="mb">支払い方法</Font>
              <div>
                <div css={box}>
                  <div css={flexBoxContent(16)}>
                    <Font>クレジットカード</Font>
                    <Button
                      variant="secondary"
                      endIcon={{
                        name: "openInNew",
                      }}
                      onClick={() => window.open(cardRegistrationUrl, "_blank")}
                    >
                      <Font variant="mb">カード情報変更・確認</Font>
                    </Button>
                  </div>
                  {reversalNgAlerts.length > 0 && (
                    <div css={alertBox}>
                      <Alert alerts={reversalNgAlerts} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    );
  } else {
    return <NoContract company={company} />;
  }
};

const container = css({
  display: "flex",
  flexDirection: "column",
  gap: "24px",
  marginTop: "16px",
});

const containerItem = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const box = css({
  border: `1px solid ${color.gray300}`,
  borderRadius: "4px",
  padding: "16px",
});

const flexBox = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const flexBoxContent = (gap: number) =>
  css({
    display: "flex",
    gap: `${gap}px`,
    alignItems: "center",
  });

const flexBoxContentText = css({
  display: "flex",
  gap: "8px",
  alignItems: "baseline",
});

const linkButton = css({
  color: color.link,
  whiteSpace: "nowrap",
  "&:hover": {
    cursor: "pointer",
    textDecoration: "underline",
  },
});

const smallBadge = css({
  width: "56px",
});

const largeBadge = css({
  width: "80px",
});

const overlay = css({
  position: "fixed",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  top: 0,
  left: 0,
  zIndex: 20,
  backgroundColor: "#00000080",
});

const modalContainer = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  marginTop: "24px",
  paddingLeft: "16px",
  paddingRight: "16px",
});

const modalList = css({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
});

const border = css({
  borderTop: `1px solid ${color.gray600}`,
});

const modalBottom = css({
  textAlign: "right",
});

const alertBox = css({
  marginTop: "8px",
});
